/**
 * Trazer um jogo de RPG Maker MZ inteiro para dentro do acervo.
 *
 * O OZ Emblem nasceu da conversão de um jogo só, feita por linha de comando por
 * quem tinha o projeto em disco. Esta tela é a mesma conversão, para qualquer
 * outro projeto e para quem só tem o .zip: mapas, tilesets, sprites, retratos,
 * battlers, trilha, efeitos, classes, personagens, inimigos, armas e itens
 * entram todos, sem apagar nada do que já existe.
 *
 * A conversão leva minutos e roda solta no servidor. Esta tela não espera por
 * ela: pergunta de tempos em tempos como vai e mostra o que o conversor está
 * dizendo, linha a linha.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface PackCounts {
  readonly mapas?: number;
  readonly imagens?: number;
  readonly sons?: number;
  readonly animacoes?: number;
  readonly classes?: number;
  readonly characters?: number;
  readonly enemies?: number;
  readonly weapons?: number;
  readonly items?: number;
}

interface Pack {
  readonly slug: string;
  readonly title: string;
  readonly idBase: number;
  readonly mapIdBase: number;
  readonly counts?: PackCounts;
}

interface JobStatus {
  readonly state: 'na_fila' | 'executando' | 'concluido' | 'erro';
  readonly log?: readonly string[];
  readonly seconds?: number;
  readonly error?: string;
  readonly pack?: Pack;
}

/** De quanto em quanto tempo perguntar como vai a conversão. */
const POLL_MS = 1500;

function formatBytes(n: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export function GameImporter(): JSX.Element {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [job, setJob] = useState<JobStatus | null>(null);
  const [error, setError] = useState('');
  const [removing, setRemoving] = useState<string | null>(null);
  const [refiltering, setRefiltering] = useState(false);
  const [notice, setNotice] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const logRef = useRef<HTMLPreElement>(null);

  const loadPacks = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/packs', { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { packs: Pack[] };
      setPacks(data.packs ?? []);
    } catch {
      setPacks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPacks();
  }, [loadPacks]);

  // O registro de andamento cresce por baixo; sem isto a última linha — que é a
  // única que interessa enquanto roda — ficaria fora da vista.
  useEffect(() => {
    const box = logRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [job]);

  /** Pergunta ao servidor como vai a conversão, até ela terminar. */
  const followJob = useCallback(
    (jobId: string) => {
      let stopped = false;

      const tick = async (): Promise<void> => {
        if (stopped) return;
        try {
          const res = await fetch(`/api/admin/packs/jobs/${jobId}`, { credentials: 'include' });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const status = (await res.json()) as JobStatus;
          setJob(status);

          if (status.state === 'concluido') {
            await loadPacks();
            return;
          }
          if (status.state === 'erro') return;
        } catch {
          // Uma pergunta perdida não é a conversão perdida: ela segue no
          // servidor, e a próxima rodada reencontra o andamento.
        }
        window.setTimeout(() => void tick(), POLL_MS);
      };

      void tick();
      return () => {
        stopped = true;
      };
    },
    [loadPacks],
  );

  const submit = () => {
    if (!file || uploading) return;
    setError('');
    setJob(null);
    setUploading(true);
    setProgress(0);

    /*
     * XHR, e não fetch: um projeto de RPG Maker tem centenas de megabytes, e
     * `fetch` não sabe dizer quanto já subiu. Sem a barra, os dois minutos de
     * envio são indistinguíveis de uma tela travada.
     */
    const form = new FormData();
    form.append('arquivo', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/admin/packs');
    xhr.withCredentials = true;

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      setUploading(false);
      let payload: { jobId?: string; error?: { message?: string } } = {};
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        payload = {};
      }

      if (xhr.status >= 200 && xhr.status < 300 && payload.jobId) {
        setJob({ state: 'na_fila', log: ['Arquivo recebido. Iniciando a conversão…'] });
        followJob(payload.jobId);
        setFile(null);
        if (inputRef.current) inputRef.current.value = '';
      } else {
        setError(payload.error?.message ?? `O envio falhou (HTTP ${xhr.status}).`);
      }
    };

    xhr.onerror = () => {
      setUploading(false);
      setError('A conexão caiu durante o envio.');
    };

    xhr.send(form);
  };

  const remove = async (pack: Pack) => {
    const confirmed = window.confirm(
      `Remover "${pack.title}" do acervo?\n\n` +
        'Os arquivos, os mapas e os registros desse jogo são apagados. ' +
        'O acervo original e os outros jogos importados não são tocados.',
    );
    if (!confirmed) return;

    setRemoving(pack.slug);
    setError('');
    try {
      const res = await fetch(`/api/admin/packs/${pack.slug}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const payload = (await res.json()) as { error?: { message?: string } };
        throw new Error(payload.error?.message ?? `HTTP ${res.status}`);
      }
      await loadPacks();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'A remoção falhou.');
    } finally {
      setRemoving(null);
    }
  };

  /**
   * Reaplica a regra de quem entra no mapa, sem reenviar o zip.
   *
   * Passa pelo servidor de propósito: os mapas de um jogo importado pelo painel
   * pertencem ao usuário do servidor web, e a mesma tarefa rodada no terminal
   * esbarra em permissão na metade do acervo.
   */
  const refilter = async () => {
    setRefiltering(true);
    setError('');
    setNotice('');
    try {
      const res = await fetch('/api/admin/packs/refilter', {
        method: 'POST',
        credentials: 'include',
      });
      const payload = (await res.json()) as { log?: string[]; error?: { message?: string } };
      if (!res.ok) throw new Error(payload.error?.message ?? `HTTP ${res.status}`);
      setNotice((payload.log ?? []).join(' ') || 'Mapas reprocessados.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'O reprocessamento falhou.');
    } finally {
      setRefiltering(false);
    }
  };

  const running = job?.state === 'na_fila' || job?.state === 'executando';

  return (
    <div className="importer-shell">
      <section className="importer-intro">
        <h2>📦 Importar um jogo de RPG Maker MZ</h2>
        <p>
          Envie o <strong>.zip</strong> da pasta de um projeto de RPG Maker MZ. Tudo o que ele
          traz — mapas, tilesets, sprites, retratos, battlers, cenários, trilha, efeitos, classes,
          personagens, inimigos, armas e itens — é convertido e somado ao acervo.
        </p>
        <p className="importer-note">
          Nada é sobrescrito: cada jogo importado recebe um prefixo próprio nos arquivos e uma
          faixa própria de identificadores, e pode ser removido inteiro depois.
        </p>
      </section>

      <section className="importer-upload">
        <label className="importer-drop">
          <input
            ref={inputRef}
            type="file"
            accept=".zip,application/zip"
            disabled={uploading || running}
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setError('');
            }}
          />
          <span className="importer-drop-icon">🗂️</span>
          <span className="importer-drop-text">
            {file ? (
              <>
                <strong>{file.name}</strong>
                <small>{formatBytes(file.size)}</small>
              </>
            ) : (
              <>
                <strong>Escolher o .zip do projeto</strong>
                <small>a pasta do jogo, com data/, img/ e audio/ dentro</small>
              </>
            )}
          </span>
        </label>

        <button
          type="button"
          className="importer-submit"
          disabled={!file || uploading || running}
          onClick={submit}
        >
          {uploading ? `Enviando… ${progress}%` : running ? 'Convertendo…' : '⬆️ Importar para o acervo'}
        </button>
      </section>

      {uploading && (
        <div className="importer-bar" role="progressbar" aria-valuenow={progress}>
          <div className="importer-bar-fill" style={{ width: `${progress}%` }} />
        </div>
      )}

      {error && <p className="importer-error">⚠️ {error}</p>}
      {notice && <p className="importer-notice">✅ {notice}</p>}

      <section className="importer-maintenance">
        <div>
          <strong>Reprocessar os mapas do acervo</strong>
          <p>
            Reaplica a regra de quem entra no mapa — só objetos e animais, e sólidos fecham a casa —
            a todos os mapas já convertidos, do acervo original e dos jogos importados. Use depois de
            mudar a regra, sem precisar reenviar nenhum zip.
          </p>
        </div>
        <button type="button" className="importer-secondary" disabled={refiltering} onClick={() => void refilter()}>
          {refiltering ? 'reprocessando…' : '🔁 Reprocessar mapas'}
        </button>
      </section>

      {job && (
        <section className={`importer-job state-${job.state}`}>
          <header>
            <strong>
              {job.state === 'concluido'
                ? '✅ Importação concluída'
                : job.state === 'erro'
                  ? '❌ A importação falhou'
                  : '⏳ Convertendo o jogo'}
            </strong>
            {typeof job.seconds === 'number' && <span className="importer-clock">{job.seconds}s</span>}
          </header>

          {job.error && <p className="importer-error">{job.error}</p>}

          {job.pack && (
            <p className="importer-summary">
              <strong>{job.pack.title}</strong> entrou como <code>{job.pack.slug}</code> ·{' '}
              {job.pack.counts?.mapas ?? 0} mapas · {job.pack.counts?.imagens ?? 0} imagens ·{' '}
              {job.pack.counts?.sons ?? 0} sons · {job.pack.counts?.classes ?? 0} classes ·{' '}
              {job.pack.counts?.characters ?? 0} personagens · {job.pack.counts?.enemies ?? 0} inimigos
            </p>
          )}

          <pre ref={logRef} className="importer-log">
            {(job.log ?? []).join('\n')}
          </pre>
        </section>
      )}

      <section className="importer-list">
        <h3>Jogos no acervo</h3>
        {loading ? (
          <p className="muted">carregando…</p>
        ) : packs.length === 0 ? (
          <p className="muted">
            Só o acervo original (Saga de Distortidus). Nenhum outro jogo foi importado ainda.
          </p>
        ) : (
          <table className="importer-table">
            <thead>
              <tr>
                <th>Jogo</th>
                <th>Apelido</th>
                <th>Conteúdo</th>
                <th>Faixa de IDs</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {packs.map((pack) => (
                <tr key={pack.slug}>
                  <td><strong>{pack.title}</strong></td>
                  <td><code>{pack.slug}</code></td>
                  <td className="importer-counts">
                    {pack.counts?.mapas ?? 0} mapas · {pack.counts?.imagens ?? 0} imagens ·{' '}
                    {pack.counts?.sons ?? 0} sons · {pack.counts?.classes ?? 0} classes ·{' '}
                    {pack.counts?.characters ?? 0} personagens
                  </td>
                  <td className="mono">
                    {pack.idBase + 1}–{pack.idBase + 1000} · mapas {pack.mapIdBase + 1}+
                  </td>
                  <td>
                    <button
                      type="button"
                      className="importer-remove"
                      disabled={removing === pack.slug}
                      onClick={() => void remove(pack)}
                    >
                      {removing === pack.slug ? 'removendo…' : '🗑️ Remover'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
